import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Users, Building2, MapPin, GraduationCap, Trophy, Briefcase, ChevronDown, CheckCircle, X } from 'lucide-react';

const SignUpRoles = () => {
  const [expandedRole, setExpandedRole] = useState(null);
  const [expandedEntity, setExpandedEntity] = useState(null);

  const roles = [
    {
      id: 'player',
      title: 'PLAYER',
      subtitle: 'Sports Enthusiasts',
      description: 'Perfect for sport enthusiasts searching for players, crew, clubs and venues for their next game.',
      image: 'https://images.unsplash.com/photo-1748111405476-52324b9cc79d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjBzcG9ydHMlMjBhY3Rpb24lMjBwbGF5ZXJ8ZW58MHx8fHwxNzc1NzM5NzUzfDA&ixlib=rb-4.1.0&q=85',
      icon: User,
      color: 'from-primary/80',
      benefits: [
        'Create your sports profile showcasing your skills, preferred sports, and experience level',
        'Find and connect with players near you based on location, sport, and skill level',
        'Post cards to find teammates for your next game or practice session',
        'Discover and book venues, courts, and turfs in your area',
        'Join clubs and teams looking for new members',
        'Get notified when players nearby are looking for a game',
        'Rate and review other players, venues, and crews',
        'Track your game history and build your sports reputation',
      ],
    },
    {
      id: 'crew',
      title: 'CREW',
      subtitle: 'Individuals & Service Providers',
      description: 'For coaches, referees, trainers, physiotherapists, and sports service organizations.',
      image: 'https://static.prod-images.emergentagent.com/jobs/e8b39f20-ada1-4d28-b88c-fa6cc56a8377/images/5f05ea05e5bce0959b7504cf39d96b5630cbe9e5a628f2ef83dffe25f4a72a56.png',
      icon: Users,
      color: 'from-secondary/80',
      benefits: [
        'Build a professional profile with your credentials, certifications, and experience',
        'Offer your services to players, clubs, and academies directly',
        'Set your own rates, availability, and service packages',
        'Receive direct booking requests from players and organizations',
        'Get discovered by thousands of sports enthusiasts in your area',
        'Manage your schedule and appointments through the platform',
        'Collect reviews and ratings to build your professional reputation',
        'Expand your client base across multiple sports and locations',
        'Perfect for: Coaches, Trainers, Referees, Umpires, Physiotherapists, Sports Analysts, Nutritionists, and Sports Service Companies',
      ],
    },
    {
      id: 'entities',
      title: 'SPORTS ENTITIES',
      subtitle: 'Organizations & Facilities',
      description: 'For clubs, academies, venues, and professional sports bodies.',
      image: 'https://static.prod-images.emergentagent.com/jobs/e8b39f20-ada1-4d28-b88c-fa6cc56a8377/images/aca6fc9b86af9a13eecabef93ef0f5e8531c407d12f44a9a88c8fe79940da581.png',
      icon: Building2,
      color: 'from-accent/80',
      isEntity: true,
      subCategories: [
        {
          id: 'clubs',
          title: 'Clubs',
          icon: Trophy,
          description: 'Sports clubs and teams',
          benefits: [
            'Create and manage your club profile with team details and achievements',
            'Add and manage club members from the Playmatez community',
            'Find emergency players when your regulars can\'t make it',
            'Challenge other clubs for friendly matches and tournaments',
            'Discover and book venues for your scheduled matches and practice',
            'Connect with professional crew (coaches, referees) for your games',
            'Organize events, tournaments, and recruitment drives',
            'Track match results and maintain club statistics',
          ],
        },
        {
          id: 'academies',
          title: 'Academies',
          icon: GraduationCap,
          description: 'Sports training academies',
          benefits: [
            'Showcase your academy\'s programs, facilities, and coaching staff',
            'Attract new students through the platform\'s reach',
            'List your training programs, batches, and fee structures',
            'Manage student enrollment and batch schedules',
            'Hire qualified coaches and trainers through the platform',
            'Organize trials, camps, and selection events',
            'Partner with venues for training facilities',
            'Build your academy\'s reputation with reviews and success stories',
          ],
        },
        {
          id: 'venues',
          title: 'Venues',
          icon: MapPin,
          description: 'Sports facilities and turfs',
          benefits: [
            'List your venue with photos, amenities, and pricing details',
            'Get discovered by 10,000+ active players and clubs',
            'Manage all bookings through a single dashboard',
            'Set slot availability, pricing, and booking rules',
            'Reduce no-shows with automated reminders',
            'Accept secure online payments through the platform',
            'Promote special offers and events to nearby players',
            'Build trust with verified reviews from users',
          ],
        },
        {
          id: 'professional',
          title: 'Professional Bodies',
          icon: Briefcase,
          description: 'Sports associations and federations',
          benefits: [
            'Establish your organization\'s official presence on the platform',
            'Organize and manage official tournaments and leagues',
            'Register and verify players, officials, and clubs',
            'Maintain player databases and rankings',
            'Announce events, rule changes, and official communications',
            'Connect with venues for hosting official events',
            'Manage official certifications for coaches and referees',
            'Promote grassroots development programs',
          ],
        },
      ],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  const handleLearnMore = (roleId) => {
    setExpandedRole(expandedRole === roleId ? null : roleId);
    setExpandedEntity(null);
  };

  const handleEntityClick = (entityId) => {
    setExpandedEntity(expandedEntity === entityId ? null : entityId);
  };

  return (
    <section id="signup" className="py-24 relative" data-testid="signup-section">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">
            Join The Community
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
            Sign Up As
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Choose your role and become part of India's largest sports networking community
          </p>
        </motion.div>

        {/* Roles Grid */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
        >
          {roles.map((role) => {
            const Icon = role.icon;
            const isExpanded = expandedRole === role.id;
            
            return (
              <motion.div
                key={role.id}
                variants={itemVariants}
                className="relative"
              >
                {/* Main Card */}
                <div
                  className={`group relative overflow-hidden rounded-2xl border transition-all duration-300 ${
                    isExpanded 
                      ? 'border-primary bg-card' 
                      : 'border-white/10 bg-card hover:border-white/20'
                  }`}
                  data-testid={`signup-card-${role.id}`}
                >
                  {/* Background Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={role.image}
                      alt={role.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${role.color} via-black/60 to-transparent`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {/* Icon & Title */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading text-xl font-bold text-white">
                          {role.title}
                        </h3>
                        <p className="text-primary text-sm">{role.subtitle}</p>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-muted-foreground text-sm mb-4">
                      {role.description}
                    </p>

                    {/* Learn More Button */}
                    <button
                      onClick={() => handleLearnMore(role.id)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-medium transition-colors"
                      data-testid={`learn-more-${role.id}`}
                    >
                      {isExpanded ? 'Show Less' : 'Learn More'}
                      <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                  </div>

                  {/* Expanded Content */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 border-t border-white/10 pt-4">
                          {role.isEntity ? (
                            // Sports Entities Sub-categories
                            <div className="space-y-3">
                              <p className="text-sm text-muted-foreground mb-4">
                                Select your organization type:
                              </p>
                              {role.subCategories.map((entity) => {
                                const EntityIcon = entity.icon;
                                const isEntityExpanded = expandedEntity === entity.id;
                                
                                return (
                                  <div key={entity.id} className="bg-white/5 rounded-xl overflow-hidden">
                                    <button
                                      onClick={() => handleEntityClick(entity.id)}
                                      className="w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-colors"
                                      data-testid={`entity-${entity.id}`}
                                    >
                                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                        <EntityIcon className="w-5 h-5 text-primary" />
                                      </div>
                                      <div className="flex-1 text-left">
                                        <p className="font-semibold text-white">{entity.title}</p>
                                        <p className="text-xs text-muted-foreground">{entity.description}</p>
                                      </div>
                                      <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isEntityExpanded ? 'rotate-180' : ''}`} />
                                    </button>
                                    
                                    <AnimatePresence>
                                      {isEntityExpanded && (
                                        <motion.div
                                          initial={{ height: 0, opacity: 0 }}
                                          animate={{ height: 'auto', opacity: 1 }}
                                          exit={{ height: 0, opacity: 0 }}
                                          className="overflow-hidden"
                                        >
                                          <div className="px-4 pb-4 space-y-2">
                                            {entity.benefits.map((benefit, i) => (
                                              <div key={i} className="flex items-start gap-2">
                                                <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                                <span className="text-sm text-muted-foreground">{benefit}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </motion.div>
                                      )}
                                    </AnimatePresence>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            // Regular benefits list
                            <div className="space-y-3">
                              <p className="text-sm font-semibold text-white mb-3">What you can do:</p>
                              {role.benefits.map((benefit, i) => (
                                <div key={i} className="flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                  <span className="text-sm text-muted-foreground">{benefit}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* CTA */}
                          <a
                            href="https://download.playmatez.io/u/F4AE8rel"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary hover:bg-primary/90 rounded-xl text-white font-semibold transition-colors"
                            data-testid={`signup-cta-${role.id}`}
                          >
                            Sign Up as {role.title}
                          </a>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default SignUpRoles;
